import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { AgentVersion, AgentVersionStatus } from "src/clients/agent-management";
import { UserNameInfo } from "src/flowContainer/versionEditedInfo/UsernameInfo";
import { formatDate } from "src/utils/datetime";

type Props = {
  version: AgentVersion;
  selected?: boolean;
  onClick: () => void;
};

const StatusIndicator: React.FC<{ status: AgentVersionStatus }> = ({
  status,
}) => {
  const isPublished = status === AgentVersionStatus.PUBLISHED;
  return (
    <Icon
      color={isPublished ? "text-green-400" : "text-yellow-400"}
      icon={faCircle}
      size="3xs"
    />
  );
};

type FooterProps = {
  userId: string;
  isPublished: boolean;
  date: string;
};

const VersionCardFooter: React.FC<FooterProps> = ({
  date,
  userId,
  isPublished,
}) => {
  const formattedDate = date !== "" ? formatDate(date) : null;

  const editInfo = (
    <>
      <div className="flex text-gray-800">
        <UserNameInfo userId={userId || ""} withAvatar />
      </div>
      {formattedDate && (
        <>
          on
          <span className="text-gray-800">{formattedDate}</span>
        </>
      )}
    </>
  );

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 text-gray-600 font-inter-normal-12px"
      data-loc="agent-version-editor-info"
    >
      {isPublished ? (
        <>published by {editInfo}</>
      ) : (
        <>last edited by {editInfo}</>
      )}
    </div>
  );
};

export const AgentVersionCard: React.FC<Props> = ({
  selected,
  version,
  onClick,
}) => {
  const isPublished = version.status === AgentVersionStatus.PUBLISHED;
  const date = isPublished
    ? version.metadata.published_at
    : version.metadata.updated_at;
  const userId = isPublished
    ? version.metadata.published_by_id || version.metadata.created_by_id
    : version.metadata.updated_by_id || version.metadata.created_by_id;

  return (
    <div
      className={twJoin(
        "cursor-pointer rounded-lg border p-4",
        selected ? "border-indigo-400" : "border-gray-200",
      )}
      data-loc={`agent-version-card-${version.name}`}
      tabIndex={-1}
      onClick={onClick}
    >
      <div className="mb-2 flex items-center gap-x-1">
        <StatusIndicator status={version.status} />
        <span className="text-gray-800 font-inter-semibold-13px">
          {version.name}
        </span>
      </div>
      <p
        className={twJoin(
          "leading-5 mb-4 line-clamp-2 font-inter-normal-12px",
          version.description ? "text-gray-500" : "text-gray-400",
        )}
      >
        {version.description || "No description"}
      </p>
      <VersionCardFooter
        date={date || ""}
        isPublished={isPublished}
        userId={userId || ""}
      />
    </div>
  );
};
